import { useEffect, useState } from 'react';

/**
 * Public device-routing landing for the mobile-app QR / links.
 *
 * The Mobile App card's QR encodes this page's URL. When a phone opens it we
 * detect the OS and forward to the matching store; desktop / unknown devices
 * get a chooser with both badges (no forced redirect).
 *
 * Must stay a PUBLIC route (no auth) — it's opened by unauthenticated phones.
 */

const APP_STORE_URL = 'https://apps.apple.com/in/app/wenext/id6760423918';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ai.wenext.app';
const we_next_logo = '/assets/wenext_logo_dark.png';

type Platform = 'ios' | 'android' | 'other';

/** Best-effort OS detection, incl. iPadOS 13+ (which reports as desktop Safari). */
function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || (navigator as any).vendor || '';
  if (/android/i.test(ua)) return 'android';
  const iOSClassic = /iPad|iPhone|iPod/.test(ua);
  const iPadOS =
    /Macintosh/.test(ua) &&
    typeof document !== 'undefined' &&
    (navigator.maxTouchPoints ?? 0) > 1;
  if (iOSClassic || iPadOS) return 'ios';
  return 'other';
}

export default function GetApp() {
  const [platform] = useState<Platform>(detectPlatform);
  // On mobile we redirect immediately; if the store app/redirect is slow we
  // still show the manual badges as a fallback after a moment.
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const target =
      platform === 'ios' ? APP_STORE_URL : platform === 'android' ? PLAY_STORE_URL : null;
    if (!target) return; // desktop / unknown → show chooser (no redirect)
    // replace() so the back button doesn't return to this bounce page.
    window.location.replace(target);
    const t = window.setTimeout(() => setShowFallback(true), 1500);
    return () => window.clearTimeout(t);
  }, [platform]);

  const redirecting = platform !== 'other';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F5F7F9] px-6 py-10 text-center">
      <div className="w-full max-w-sm rounded-2xl border border-[#E4E7EC] bg-white p-8 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
        <img src={we_next_logo} alt="wenext" className="mx-auto mb-6 w-[132px]" />

        {redirecting && !showFallback ? (
          <>
            <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-4 border-[#EAECF0] border-t-[var(--secondary-color,#1AB169)]" />
            <p className="text-[15px] font-semibold text-[#101828]">Opening the store…</p>
            <p className="mt-1 text-[13px] text-[#6B7280]">
              Taking you to the {platform === 'ios' ? 'App Store' : 'Google Play'}.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-[18px] font-semibold text-[#101828]">Get the WeNext app</h1>
            <p className="mt-1 text-[13px] leading-relaxed text-[#6B7280]">
              Reply to conversations, get lead notifications and trigger flows from your phone.
            </p>
          </>
        )}

        {/* Store badges — always available as a manual fallback. */}
        <div className="mt-6 flex flex-col items-stretch gap-2.5">
          <a
            href={APP_STORE_URL}
            className="flex items-center justify-center gap-2.5 rounded-xl bg-[#101828] px-4 py-2.5 text-white transition hover:bg-[#0F172A]"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="h-6 w-6">
              <path d="M17.3 12.5c0-2.4 2-3.5 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.6.9-.8 0-1.9-.9-3.2-.8-1.6.1-3.2 1-4 2.5-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.4 1.3 0 1.7-.8 3.2-.8 1.5 0 1.9.8 3.2.8 1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.5-1-2.6-3.9zM14.9 5.3c.7-.8 1.1-2 1-3.1-1 .1-2.1.6-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" />
            </svg>
            <span className="text-left leading-tight">
              <span className="block text-[10px] uppercase tracking-wider text-white/70">Download on the</span>
              <span className="block text-[14px] font-semibold">App Store</span>
            </span>
          </a>
          <a
            href={PLAY_STORE_URL}
            className="flex items-center justify-center gap-2.5 rounded-xl bg-[#101828] px-4 py-2.5 text-white transition hover:bg-[#0F172A]"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="h-6 w-6">
              <path d="M4 2.2v19.6c0 .8.9 1.3 1.6.9l14-9.8c.7-.5.7-1.4 0-1.9l-14-9.8C4.9.9 4 1.4 4 2.2z" />
            </svg>
            <span className="text-left leading-tight">
              <span className="block text-[10px] uppercase tracking-wider text-white/70">Get it on</span>
              <span className="block text-[14px] font-semibold">Google Play</span>
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}
